import { useTranslation } from 'react-i18next';
import { Sparkles } from 'lucide-react';
import type {
  Signal,
  SignalRelatedIssue,
  ResolvedRelevantParty,
  SignalApplyOperation,
} from '@momentum/types';
import { SignalCategory } from '@momentum/types';
import type { SignalApplicabilityScope } from '@momentum/types';
import { Badge } from '@/shared/components/ui/Badge';
import { useSignalsByIds } from '@/shared/hooks/useSignals';
import type { EmployeeInfo } from '../../constants';
import { MentionText } from '@/shared/components/mention-text/MentionText';
import { ApplicabilityScopeSection } from './ApplicabilityScopeSection';
import { SignalDetailMetaBar } from './SignalDetailMetaBar';
import { SignalLinkedIssues } from './SignalLinkedIssues';
import { SignalActivityTimeline } from './SignalActivityTimeline';

interface SignalDetailContentProps {
  signal: Signal;
  isActive: boolean;
  isCreator: boolean;
  installedCount: number;
  author: EmployeeInfo | undefined;
  relatedIssues: SignalRelatedIssue[] | undefined;
  employeeMap: Map<string, EmployeeInfo>;
  resolvedRelevantParties?: ResolvedRelevantParty[];
  // Applicability scope props
  isManager: boolean;
  currentApplyOperation: SignalApplyOperation | undefined;
  directReportsCount: number;
  fullHierarchyCount: number;
  onApplyScope: (scope: SignalApplicabilityScope) => void;
  onChangeScope: (newScope: SignalApplicabilityScope) => void;
  isApplying: boolean;
  appliedByName?: string;
}

export function SignalDetailContent({
  signal,
  isActive,
  isCreator,
  installedCount,
  author,
  relatedIssues,
  employeeMap,
  resolvedRelevantParties,
  isManager,
  currentApplyOperation,
  directReportsCount,
  fullHierarchyCount,
  onApplyScope,
  onChangeScope,
  isApplying,
  appliedByName,
}: SignalDetailContentProps) {
  const { t } = useTranslation('knowledge');

  const informedByIds = signal.informedBy ?? [];
  const { data: informedBySignals } = useSignalsByIds(informedByIds, {
    enabled: informedByIds.length > 0,
  });

  return (
    <div className="flex flex-col border-e px-7 py-6">
      {/* Category badge */}
      <div className="flex items-center gap-2">
        {signal.category === SignalCategory.COMMITMENT ? (
          <Badge
            variant="outline"
            className="bg-amber-50 text-amber-700 border-amber-200"
          >
            {t('categoryCommitment')}
          </Badge>
        ) : (
          <Badge
            variant="outline"
            className="bg-sky-50 text-sky-700 border-sky-200"
          >
            {t('categoryPlaybook')}
          </Badge>
        )}
      </div>

      {/* Signal text */}
      <p className="py-6 text-[15px] leading-[1.75] text-foreground">
        <MentionText
          title={signal.title}
          resolvedRelevantParties={resolvedRelevantParties}
        />
      </p>

      {/* Informed by section (for specific agents) */}
      {informedBySignals && informedBySignals.length > 0 && (
        <div className="mb-4 rounded-lg border border-amber-100 bg-amber-50/50 p-3">
          <div className="mb-2 flex items-center gap-1.5 text-xs font-medium text-amber-700">
            <Sparkles className="h-3.5 w-3.5" />
            {t('informedByLabel')}
          </div>
          <ul className="space-y-1">
            {informedBySignals.map((informingSignal) => (
              <li
                key={informingSignal._id}
                className="text-sm text-amber-900/80"
              >
                {informingSignal.title}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Applicability scope section */}
      <ApplicabilityScopeSection
        currentApplyOperation={currentApplyOperation}
        isActive={isActive}
        isManager={isManager}
        directReportsCount={directReportsCount}
        fullHierarchyCount={fullHierarchyCount}
        onApplyScope={onApplyScope}
        onChangeScope={onChangeScope}
        isApplying={isApplying}
      />

      {/* Meta info bar */}
      <SignalDetailMetaBar
        origin={signal.origin}
        installedCount={installedCount}
        isActive={isActive}
        appliedByName={appliedByName}
      />

      {/* Linked Issues */}
      <SignalLinkedIssues
        relatedIssues={relatedIssues}
        employeeMap={employeeMap}
      />

      {/* Activity timeline */}
      <SignalActivityTimeline
        author={author}
        isAnonymous={!!signal.isAnonymous}
        isCreator={isCreator}
        createdAt={signal.metadata?.createdAt}
        lastUpdatedAt={signal.metadata?.lastUpdatedAt}
      />
    </div>
  );
}
